import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";

import { useInfiniteMediaLibraryAssets } from "@/api/products";
import {
  MEDIA_TAB_LIBRARY_PARAMS,
  mediaLibraryFilterForCollection,
  type SKUStoreCollection,
} from "@/features/SKUStore/collections/skuStoreCollections";
import { useCollectionProductCards } from "@/features/SKUStore/collections/useCollectionProductCards";
import {
  matchesQuery,
  mediaCardMeta,
  mediaCardSubtitle,
  mediaCardTitle,
} from "@/features/SKUStore/components/Collections/mediaCard";
import { SKUStorePrepareMediaBanner } from "@/features/SKUStore/components/Collections/SKUStorePrepareMediaBanner";
import { SKUStoreResourceCardMedia } from "@/features/SKUStore/components/ResourceCard/SKUStoreResourceCardMedia";
import { SearchIcon as Search } from "@/icons";
import { MuiButton } from "@/orion/MuiComponents/MuiButton";
import { MuiTextField } from "@/orion/MuiComponents/MuiTextField";

const cardGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    sm: "repeat(auto-fill, minmax(240px, 1fr))",
  },
  gap: 3,
  maxWidth: "100%",
} as const;

export type SKUStoreCollectionMediaTabProps = {
  siteId: string;
  collection: SKUStoreCollection;
  /** Opens the CreativeSphere pilot flow scoped to this collection/category. */
  onPrepareMedia?: () => void;
};

export function SKUStoreCollectionMediaTab({
  siteId,
  collection,
  onPrepareMedia,
}: SKUStoreCollectionMediaTabProps) {
  const [query, setQuery] = useState("");

  const params = useMemo(
    () => ({
      ...MEDIA_TAB_LIBRARY_PARAMS,
      ...mediaLibraryFilterForCollection(collection),
    }),
    [collection],
  );

  const {
    data,
    isLoading,
    isError,
    isFetchNextPageError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteMediaLibraryAssets(siteId, { params });

  const assets = useMemo(
    () => data?.pages.flatMap((page) => page.assets) ?? [],
    [data],
  );
  const totalCount = data?.pages[0]?.count ?? assets.length;

  // Server-side total (not the loaded page size); same query as the Products tab.
  const { data: productPages } = useCollectionProductCards(siteId, collection);
  const productCount = productPages?.pages[0]?.count ?? collection.productCount;

  const { ref: loadMoreRef, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
      void fetchNextPage();
    }
  }, [
    inView,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  ]);

  const filteredAssets = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return assets;
    return assets.filter((asset) => matchesQuery(asset, trimmed));
  }, [assets, query]);

  return (
    <Stack spacing={4} sx={{ px: 3, pt: 4, pb: 5, minWidth: 0 }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        Media ({totalCount})
      </Typography>

      <SKUStorePrepareMediaBanner
        collectionName={collection.name}
        productCount={productCount}
        onOpen={onPrepareMedia}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        <MuiTextField
          size="small"
          fullWidth
          placeholder="Search media"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoComplete="off"
          inputProps={{ "aria-label": "Search collection media" }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search aria-hidden fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : isError && !data ? (
        <Typography variant="body2" color="error">
          Could not load media.
        </Typography>
      ) : (
        <>
          {filteredAssets.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {assets.length === 0
                ? `No media for ${collection.name} yet.`
                : "No media matches your search."}
            </Typography>
          ) : (
            <Box sx={cardGridSx}>
              {filteredAssets.map((asset) => (
                <SKUStoreResourceCardMedia
                  key={asset.id}
                  layout="media"
                  imageAlt={asset.alt || asset.title || asset.product_title}
                  imageSrc={asset.url}
                  // SKU Store never shows documents (see SKUStoreMediaType).
                  mediaType={asset.type === "document" ? undefined : asset.type}
                  meta={mediaCardMeta(asset)}
                  subtitle={mediaCardSubtitle(asset)}
                  title={mediaCardTitle(asset)}
                />
              ))}
            </Box>
          )}
          {hasNextPage && (
            <Box
              ref={loadMoreRef}
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 3,
                minHeight: 40,
              }}
            >
              {isFetchingNextPage ? (
                <CircularProgress size={24} />
              ) : isFetchNextPageError ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="error">
                    Could not load more media.
                  </Typography>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    onClick={() => void fetchNextPage()}
                  >
                    Retry
                  </MuiButton>
                </Stack>
              ) : null}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}
